import { useState, useEffect } from "react"
import { format, startOfWeek, addDays, endOfWeek } from "date-fns"
import { useNavigate } from "react-router-dom"
import { ProjectProps } from "../../../interfaces/project_types"
import { getProjectByDate } from "../../../api/projects"
import { OrangeButton, RouteButton } from "../../Buttons"
import { subWeeks, addWeeks } from "date-fns"
import { CalendarProps } from "../../../interfaces/calendar_type"
import { ProjectCard } from "./ProjectCard"

export function WeeklyCalendar() {
    const [currentWeek, setCurrentWeek] = useState(new Date())
    const [projectsDueThisWeek, setProjectsDueThisWeek] = useState<CalendarProps[]>([])
    
    const handlePrevWeek = () => {
        setCurrentWeek(subWeeks(currentWeek, 1))
    }

    const handleNextWeek = () => {
        setCurrentWeek(addWeeks(currentWeek, 1))
    }

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const weekStart = startOfWeek(currentWeek)
                const weekEnd = endOfWeek(currentWeek)

                // Get the start and end dates of the week
                const startDate = format(weekStart, 'yyyy-MM-dd')
                const endDate = format(weekEnd, 'yyyy-MM-dd')

                // Make a single API call with the date range
                const response = await getProjectByDate(
                    weekStart.getFullYear().toString(),
                    (weekStart.getMonth() + 1).toString(),
                    `${startDate},${endDate}`
                )

                // Convert single object to array if needed
                const allProjects = Array.isArray(response) ? response : [response]

                setProjectsDueThisWeek(Array.from({ length: 7 }, (_, index) => {
                    const currentDay = addDays(weekStart, index)
                    const formattedDate = format(currentDay, 'yyyy-MM-dd')

                    const projects_due = allProjects.filter(
                        (project) => project.end_date === formattedDate
                    )

                    return {
                        day: index,
                        projects: projects_due
                    }
                }))

            } catch (error) {
                console.error("Server Error: ", error)
            }
        }
        fetchProjects()
    }, [currentWeek])
    
    return (
        <div className="print:-mt-10">
            <div className='flex justify-center gap-4 print:hidden'>
                <OrangeButton onClick={() => setCurrentWeek(new Date())} className='p-4 transition '>
                    Go to Today
                </OrangeButton>

                <RouteButton route="/calendar/monthly" text="Switch to Monthly Calendar" />
            </div>

            <div className='p-6 mx-auto my-5 rounded-lg '>
                <div className='flex justify-between mb-4 print:hidden'>
                    <OrangeButton onClick={handlePrevWeek} className='p-4 transition '>
                        Previous
                    </OrangeButton>

                    <div className='flex justify-center'>
                        <h2 className="text-2xl font-semibold">
                            {format(currentWeek, 'MMMM yyyy')}
                        </h2>
                    </div>

                    <OrangeButton onClick={handleNextWeek} className='p-4 transition '>
                        Next
                    </OrangeButton>
                </div>

                <div className="grid grid-cols-7 gap-2">
                    <h4 className="text-center">Sun</h4>
                    <h4 className="text-center">Mon</h4>
                    <h4 className="text-center">Tue</h4>
                    <h4 className="text-center">Wed</h4>
                    <h4 className="text-center">Thu</h4>
                    <h4 className="text-center">Fri</h4>
                    <h4 className="text-center">Sat</h4>
                </div>

                <div className='grid grid-cols-7 gap-2 text-center'>
                    {Array.from({ length: 7 }, (_, index) => {
                        const currentDate = addDays(startOfWeek(currentWeek), index)
                        return (
                            <WeekDayCard 
                                key={index}
                                date={currentDate}
                                projects={projectsDueThisWeek[index]?.projects}
                                alternate_color={index % 2 === 0}
                            />
                        )
                    })}
                </div>
            </div>

            <div className="flex justify-center mt-4 print:hidden">
                <RouteButton route="/main_menu" text="Back to Main Menu" />
            </div>
        </div>
    )
}

type WeekDayCardProps = {
    date: Date
    projects?: ProjectProps[]
    alternate_color: boolean
}

export function WeekDayCard({ date, projects, alternate_color }: WeekDayCardProps) {
    return (
        <div className={`min-h-screen h-full border border-gray-300 rounded-md dark:border-stone-700 text-center print:overflow-y-hidden ${alternate_color ? 'bg-gray-200 dark:bg-stone-700' : 'bg-gray-100 dark:bg-stone-800'}`}>  
            <div className='mb-5 text-xl font-semibold'>
                {format(date, 'd')}
            </div>

            <div className='overflow-y-auto'>
                {projects && projects.length > 0 && (
                    <ListProjectsOnDayWeekly projects={projects} />
                )}
            </div>
        </div>
    )
}

/**
 * Renders all projects due on a specific day
 * @param projects list of projects on a specific day
 */
export function ListProjectsOnDayWeekly({ projects }: { projects: ProjectProps[] }) {
    const navigate = useNavigate()

    return(
        <div className='flex flex-col gap-2 overflow-y-auto'>
            {projects.map((project) => (
                <ProjectCard project={project} navigate={navigate} key={project.project_id}/>
            ))
            }
        </div>
    )
}

